/**
 * FuturEnergy – Design Tokens
 * Fonte: FuturEnergy-BrandingManual.pdf (Color Consulting 2026)
 *
 * Usa questi token in React inline-styles, Tailwind config,
 * o qualsiasi sistema CSS-in-JS.
 */

// ─────────────────────────────────────────────────────────────────
// COLORI — versione Digital/Web (RGB + HEX)
// ─────────────────────────────────────────────────────────────────
export const colors = {
  // Palette primaria logo (pag. 3 manuale)
  greenLight:  '#64BE6F',   // R100 G190 B111 — verde chiaro logo
  greenDark:   '#00A157',   // R0   G161 B87  — verde scuro logo

  // Armonie cromatiche complementari (pag. 5 manuale)
  greenPure:   '#008D4C',   // R0   G142 B77
  lime:        '#B4FF00',   // R180 G255 B0
  yellow:      '#FFD400',   // R255 G212 B0

  // Neutri
  white:       '#FFFFFF',
  offWhite:    '#F5F7F5',
  gray50:      '#F0F4F1',
  gray100:     '#E2EAE5',
  gray200:     '#C5D4CA',
  gray400:     '#8AA896',
  gray600:     '#4A635A',
  gray900:     '#0F1F18',

  // Dark surfaces
  darkBg:      '#0A1A10',
  darkSurface: '#122018',

  // Tinte con opacità (usare con rgba o CSS custom props)
  greenPure70: 'rgba(0, 142,  77, 0.70)',
  greenPure50: 'rgba(0, 142,  77, 0.50)',
  greenPure30: 'rgba(0, 142,  77, 0.30)',
  greenPure10: 'rgba(0, 142,  77, 0.10)',
  lime70:      'rgba(180, 255,   0, 0.70)',
  lime50:      'rgba(180, 255,   0, 0.50)',
  lime30:      'rgba(180, 255,   0, 0.30)',
  lime10:      'rgba(180, 255,   0, 0.10)',
  yellow70:    'rgba(255, 212,   0, 0.70)',
  yellow50:    'rgba(255, 212,   0, 0.50)',
  yellow30:    'rgba(255, 212,   0, 0.30)',
  yellow10:    'rgba(255, 212,   0, 0.10)',
} as const;

// ─────────────────────────────────────────────────────────────────
// GRADIENTI (pag. 6 manuale — diagonali 50/50, 30/70, 70/30)
// ─────────────────────────────────────────────────────────────────
export const gradients = {
  smooth:    `linear-gradient(135deg, ${colors.greenDark} 0%, ${colors.greenLight} 100%)`,
  split5050: `linear-gradient(135deg, ${colors.greenDark} 50%, ${colors.greenLight} 50%)`,
  split3070: `linear-gradient(135deg, ${colors.greenDark} 30%, ${colors.greenLight} 70%)`,
  split7030: `linear-gradient(135deg, ${colors.greenDark} 70%, ${colors.greenLight} 30%)`,
  accent:    `linear-gradient(135deg, ${colors.greenPure} 0%, ${colors.lime} 100%)`,
  dark:      `linear-gradient(135deg, ${colors.darkBg} 0%, ${colors.darkSurface} 100%)`,
} as const;

// ─────────────────────────────────────────────────────────────────
// TIPOGRAFIA — Font ufficiale: DM Sans (pag. 2 e pag. 14 manuale)
// Pesi: Regular(400), Medium(500), Bold(700)
// ─────────────────────────────────────────────────────────────────
export const typography = {
  fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",

  weight: {
    regular: 400,
    medium:  500,
    bold:    700,
  },

  /** Gerarchia dal manuale: Title1 → Title2 → Apertura → Body → Caption */
  scale: {
    hero:   'clamp(2.5rem, 6vw, 4.5rem)',   // Title 1
    h1:     'clamp(2rem, 4vw, 3rem)',        // Title 2
    h2:     '1.875rem',                      // 30px
    h3:     '1.5rem',                        // 24px
    lead:   '1.25rem',                       // Apertura — DM Sans Medium
    body:   '1rem',                          // Corpo — DM Sans Regular
    small:  '0.875rem',                      // Body small
    caption:'0.75rem',                       // Label / caption
  },

  lineHeight: {
    tight:   1.2,
    snug:    1.35,
    normal:  1.55,
    relaxed: 1.75,
  },

  letterSpacing: {
    tight:  '-0.03em',
    normal: '0',
    wide:   '0.04em',
    caps:   '0.12em',
  },
} as const;

// ─────────────────────────────────────────────────────────────────
// SPAZIATURA
// ─────────────────────────────────────────────────────────────────
export const spacing = {
  1:  '0.25rem',
  2:  '0.5rem',
  3:  '0.75rem',
  4:  '1rem',
  5:  '1.25rem',
  6:  '1.5rem',
  8:  '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
} as const;

// ─────────────────────────────────────────────────────────────────
// BORDI & RAGGI
// ─────────────────────────────────────────────────────────────────
export const radius = {
  sm:   '0.375rem',
  md:   '0.75rem',
  lg:   '1.25rem',
  xl:   '2rem',
  full: '9999px',
} as const;

// ─────────────────────────────────────────────────────────────────
// OMBRE
// ─────────────────────────────────────────────────────────────────
export const shadows = {
  xs:   '0 1px 3px rgba(0, 142, 77, 0.08)',
  sm:   '0 2px 8px rgba(0, 142, 77, 0.12)',
  md:   '0 4px 20px rgba(0, 142, 77, 0.16)',
  lg:   '0 8px 40px rgba(0, 142, 77, 0.22)',
  xl:   '0 16px 60px rgba(0, 142, 77, 0.28)',
  glow: '0 0 24px rgba(100, 190, 111, 0.45)',
} as const;

// ─────────────────────────────────────────────────────────────────
// TRANSIZIONI
// ─────────────────────────────────────────────────────────────────
export const transitions = {
  fast:   '0.15s cubic-bezier(0.16, 1, 0.3, 1)',
  normal: '0.25s cubic-bezier(0.16, 1, 0.3, 1)',
  slow:   '0.45s cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

// ─────────────────────────────────────────────────────────────────
// LAYOUT
// ─────────────────────────────────────────────────────────────────
export const layout = {
  containerMax:  '1280px',
  containerMd:   '960px',
  sidebarWidth:  '240px',
  headerHeight:  '64px',
} as const;

// ─────────────────────────────────────────────────────────────────
// REGOLE LOGO (pag. 7-12 manuale)
// ─────────────────────────────────────────────────────────────────
export const logoRules = {
  /**
   * USI CONSENTITI
   * ─────────────
   * ✅ Logo positivo (colore) su fondi bianchi / chiari
   * ✅ Logo positivo su immagini fotografiche a bassa opacità
   * ✅ Logo negativo (bianco) su fondi scuri
   * ✅ Logo negativo su fondi colorati scuri (verde scuro, dark bg)
   * ✅ Logo negativo su immagini fotografiche ad alta copertura
   *
   * USI NON CONSENTITI
   * ──────────────────
   * ❌ Non distorcere o ridimensionare in modo non proporzionale
   * ❌ Non ruotare il logo
   * ❌ Non cambiare i colori al di fuori della palette ufficiale
   * ❌ Non applicare effetti (ombre, glow, outline, ecc.)
   * ❌ Non usare il logo positivo su sfondi colorati saturi
   * ❌ Non ridurre il clearspace minimo
   *
   * CLEARSPACE MINIMO
   * ─────────────────
   * Margine minimo attorno al logo = altezza della lettera "F" del logotipo
   * Implementato con padding: 0.5em sul wrapper .fe-logo-wrapper
   */
  versions: {
    positive: 'uso su fondi chiari (white, off-white, foto bassa opacità)',
    negative: 'uso su fondi scuri, colorati scuri, foto ad alta copertura',
  },
  clearspace: '0.5em (=altezza lettera F del logotipo)',
  minSize: '24px (height)',
} as const;

// ─────────────────────────────────────────────────────────────────
// COLORI STAMPA — CMYK (pag. 3 manuale)
// (Solo riferimento — non usare in CSS web)
// ─────────────────────────────────────────────────────────────────
export const printColors = {
  greenLight: { C: 63,  M: 0,  Y: 76,  K: 0  },  // #64BE6F equivalente
  greenDark:  { C: 100, M: 3,  Y: 100, K: 93 },  // #00A157 equivalente
} as const;

// Export default aggregato
const designTokens = {
  colors,
  gradients,
  typography,
  spacing,
  radius,
  shadows,
  transitions,
  layout,
  logoRules,
  printColors,
};

export default designTokens;
